import { ChannelChatType, ChannelStatus, ChannelType } from '@app/constants/enums/channel';
import { DealRoomStatus, DealRoomType } from '@app/constants/enums/deal-room';
import { DealRoomPeopleLogAction } from '@app/constants/enums/deal-room-people-log';
import { DealRoomDepartment } from '@app/constants/enums/department';
import { UserTeamLevel } from '@app/constants/enums/user';
import { TFunction } from 'react-i18next';

export const getOptionsWithAll = (
  t: TFunction<'translation', undefined>,
  options: {
    id: string | number;
    label: string;
  }[],
  key: string | null = null,
): {
  id: string | number | '';
  label: string;
}[] => {
  return [{ id: '', label: t(key ? key : 'common.all') }, ...options];
};

export const getDealRoomStatuses = (
  t: TFunction<'translation', undefined>,
): {
  id: DealRoomStatus | '';
  label: string;
}[] => {
  return [
    { id: DealRoomStatus.OPEN_FOR_REPRESENTATIVE, label: t('dealRooms.statuses.openForRepresentative') },
    { id: DealRoomStatus.OPEN_FOR_CUSTOMER, label: t('dealRooms.statuses.openForCustomer') },
    { id: DealRoomStatus.CLOSED, label: t('dealRooms.statuses.closed') },
  ];
};

export const getDealRoomPeopleLogActions = (
  t: TFunction<'translation', undefined>,
): {
  id: DealRoomPeopleLogAction | '';
  label: string;
}[] => {
  return [
    { id: '', label: t('common.none') },
    { id: DealRoomPeopleLogAction.VIEW, label: t('dealRooms.signals.view') },
    { id: DealRoomPeopleLogAction.OPEN_FILE, label: t('dealRooms.signals.open-file') },
    { id: DealRoomPeopleLogAction.DOWNLOAD, label: t('dealRooms.signals.download') },
    { id: DealRoomPeopleLogAction.UPLOAD, label: t('dealRooms.signals.upload') },
    { id: DealRoomPeopleLogAction.UPDATE_UPLOAD, label: t('dealRooms.signals.update-upload') },
    { id: DealRoomPeopleLogAction.LINK, label: t('dealRooms.signals.link') },
    { id: DealRoomPeopleLogAction.UPDATE_LINK, label: t('dealRooms.signals.update-link') },
    { id: DealRoomPeopleLogAction.OPEN_LINK, label: t('dealRooms.signals.open-link') },
    { id: DealRoomPeopleLogAction.ADD_COMMENT, label: t('dealRooms.signals.add-comment') },
    { id: DealRoomPeopleLogAction.ADD_COMMENT_FILE, label: t('dealRooms.signals.add-comment-file') },
    { id: DealRoomPeopleLogAction.DELETE_COMMENT, label: t('dealRooms.signals.delete-comment') },
  ];
};

export const getChannelStatuses = (
  t: TFunction<'translation', undefined>,
): {
  id: ChannelStatus | '';
  label: string;
}[] => {
  return [
    { id: ChannelStatus.OPEN, label: t('chats.statuses.open') },
    { id: ChannelStatus.CLOSED, label: t('chats.statuses.close') },
  ];
};

export const getChannelTypes = (
  t: TFunction<'translation', undefined>,
): {
  id: ChannelType | '';
  label: string;
}[] => {
  return [
    { id: '', label: t('common.all') },
    { id: ChannelType.DEAL_ROOM, label: t('chats.types.deal-room') },
    { id: ChannelType.TICKET, label: t('chats.types.ticket') },
  ];
};

export const getChannelChatTypes = (
  t: TFunction<'translation', undefined>,
): {
  id: ChannelChatType | '';
  label: string;
}[] => {
  return [
    { id: ChannelChatType.ALL, label: t('chats.chatTypes.all') },
    { id: ChannelChatType.DIRECT, label: t('chats.chatTypes.direct') },
    { id: ChannelChatType.GROUP, label: t('chats.chatTypes.group') },
  ];
};

export const getUserDepartments = (
  t: TFunction<'translation', undefined>,
): {
  id: DealRoomDepartment | '';
  label: string;
}[] => {
  return [
    { id: DealRoomDepartment.SALES, label: t('dealRooms.departments.sales') },
    { id: DealRoomDepartment.INNOVATIONS_A, label: t('dealRooms.departments.innovations-a') },
    { id: DealRoomDepartment.INNOVATIONS_B, label: t('dealRooms.departments.innovations-b') },
    { id: DealRoomDepartment.CLAIMS, label: t('dealRooms.departments.claims') },
    { id: DealRoomDepartment.CUSTOMER_SERVICE, label: t('dealRooms.departments.customerService') },
    { id: DealRoomDepartment.PIPE_ARRANGEMENT, label: t('dealRooms.departments.pipeArrangement') },
    { id: DealRoomDepartment.COLLECTION, label: t('dealRooms.departments.collection') },
    { id: DealRoomDepartment.MANAGEMENT_COMPANIES, label: t('dealRooms.departments.managementCompanies') },
  ];
};

export const getUserTeamLevels = (
  t: TFunction<'translation', undefined>,
): {
  id: UserTeamLevel | '';
  label: string;
}[] => {
  return [
    { id: UserTeamLevel.LEVEL_1, label: t('workers.teamLevels.levelOne') },
    { id: UserTeamLevel.LEVEL_2, label: t('workers.teamLevels.levelTwo') },
    { id: UserTeamLevel.LEVEL_3, label: t('workers.teamLevels.levelThree') },
  ];
};

export const getDealRoomTypes = (
  t: TFunction<'translation', undefined>,
): {
  id: DealRoomType | '';
  label: string;
}[] => {
  return [
    { id: DealRoomType.NEW_OFFER, label: t('dealRooms.types.newOffer') },
    { id: DealRoomType.RENEWAL, label: t('dealRooms.types.renewal') },
    { id: DealRoomType.CLAIM, label: t('dealRooms.types.claim') },
    { id: DealRoomType.MANAGEMENT_COMPANY, label: t('dealRooms.types.managementCompany') },
    { id: DealRoomType.COLLECTION, label: t('dealRooms.types.collection') },
    { id: DealRoomType.SERVICE, label: t('dealRooms.types.service') },
  ];
};

export const getDealRoomStatus = (
  t: TFunction<'translation', undefined>,
  status: DealRoomStatus | null | undefined,
): string => {
  const statuses = getDealRoomStatuses(t);
  const type = statuses.find((type) => type.id === status);
  return type?.label || t('common.none');
};

export const getDealRoomPeopleLogAction = (
  t: TFunction<'translation', undefined>,
  action: DealRoomPeopleLogAction | null | undefined,
): string => {
  const actions = getDealRoomPeopleLogActions(t);
  const type = actions.find((type) => type.id === action);
  return type?.label || t('common.none');
};

export const getChannelStatusLabel = (
  t: TFunction<'translation', undefined>,
  status: ChannelStatus | null | undefined,
): string => {
  const statuses = getChannelStatuses(t);
  const type = statuses.find((type) => type.id === status);
  return type?.label || t('common.none');
};

export const getChannelTypeLabel = (
  t: TFunction<'translation', undefined>,
  cType: ChannelType | null | undefined,
): string => {
  const types = getChannelTypes(t);
  const typeLabel = types.find((type) => type.id === cType);
  return typeLabel?.label || t('common.all');
};

export const getUserDepartmentLabel = (
  t: TFunction<'translation', undefined>,
  department: DealRoomDepartment | null | undefined,
): string => {
  const departments = getUserDepartments(t);
  const dept = departments.find((type) => type.id === department);
  return dept?.label || t('common.none');
};

export const getUserTeamLevelLabel = (
  t: TFunction<'translation', undefined>,
  level: UserTeamLevel | null | undefined,
): string => {
  const levels = getUserTeamLevels(t);
  const lvl = levels.find((type) => type.id === level);
  return lvl?.label || t('common.none');
};

export const getDealRoomTypeLabel = (
  t: TFunction<'translation', undefined>,
  roomType: DealRoomType | null | undefined,
): string => {
  const types = getDealRoomTypes(t);
  const typeLabel = types.find((type) => type.id === roomType);
  return typeLabel?.label || t('common.none');
};
